import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Paper,
    CircularProgress,
    Alert,
    Autocomplete,
    TextField,
    List,
    ListItem,
    ListItemText,
    Button,
} from '@mui/material';
import { Edit as EditIcon } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { businessService } from '../services/businessService';
import { customUserDataFieldService } from '../services/customUserDataFieldService';
import { BusinessUser } from '../types/business';
import { Business } from '../types/auth';
import { UserDataFieldDto, BusinessDataFieldDto, BusinessDataFieldType } from '../types/customUserDataFields';
import CustomFieldDialog from '../components/account/CustomFieldDialog';
import { extractApiErrorMessage } from '../utils/apiUtils';
import { sortUsers } from '../utils/userHelpers';

function buildMergedFields(
    userFields: UserDataFieldDto[],
    businessFields: BusinessDataFieldDto[],
    userId: string
): UserDataFieldDto[] {
    const userFieldMap = new Map(
        userFields.map((f: UserDataFieldDto) => [f.businessDataFieldId, f])
    );
    return businessFields.map((bField: BusinessDataFieldDto) => {
        const existingField = userFieldMap.get(bField.id);
        if (existingField) return existingField;
        return {
            id: '',
            userId,
            businessDataFieldId: bField.id,
            value: '',
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            businessDataField: bField,
        };
    });
}

const AdminUserDataPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();

    const [businesses, setBusinesses] = useState<Business[]>([]);
    const [selectedBusiness, setSelectedBusiness] = useState<Business | null>(null);
    const [businessUsers, setBusinessUsers] = useState<BusinessUser[]>([]);
    const [selectedUser, setSelectedUser] = useState<BusinessUser | null>(null);
    const [fields, setFields] = useState<UserDataFieldDto[]>([]);
    const [businessFields, setBusinessFields] = useState<BusinessDataFieldDto[]>([]);
    const [isLoadingBusinesses, setIsLoadingBusinesses] = useState(true);
    const [isLoadingUsers, setIsLoadingUsers] = useState(false);
    const [isLoadingFields, setIsLoadingFields] = useState(false);
    const [error, setError] = useState('');
    const [editingField, setEditingField] = useState<UserDataFieldDto | null>(null);

    // Redirect non-admins
    useEffect(() => {
        if (user && !user.isAdmin) {
            navigate('/dashboard');
        }
    }, [user, navigate]);

    // Load businesses
    useEffect(() => {
        const loadBusinesses = async () => {
            try {
                setIsLoadingBusinesses(true);
                setError('');
                const allBusinesses = await businessService.getAllBusinesses();
                setBusinesses(allBusinesses);

                // Auto-select if only one business, or select the user's business
                if (allBusinesses.length === 1) {
                    setSelectedBusiness(allBusinesses[0]);
                } else if (user?.businessId) {
                    const userBusiness = allBusinesses.find(b => b.id === user.businessId);
                    if (userBusiness) {
                        setSelectedBusiness(userBusiness);
                    }
                }
            } catch (err) {
                console.error('Error loading businesses:', err);
                setError(extractApiErrorMessage(err, 'Failed to load businesses'));
            } finally {
                setIsLoadingBusinesses(false);
            }
        };
        loadBusinesses();
    }, [user?.businessId]);

    // Load users and business data fields when business changes
    useEffect(() => {
        if (!selectedBusiness) {
            setBusinessUsers([]);
            setSelectedUser(null);
            setBusinessFields([]);
            setFields([]);
            return;
        }

        const loadBusinessData = async () => {
            try {
                setIsLoadingUsers(true);
                setSelectedUser(null);
                setFields([]);
                setError('');

                const [users, bFields] = await Promise.all([
                    businessService.getBusinessUsers(selectedBusiness.id),
                    customUserDataFieldService.getBusinessDataFieldsForBusiness(selectedBusiness.id),
                ]);

                setBusinessUsers(sortUsers(users));
                setBusinessFields(bFields);
            } catch (err) {
                console.error('Error loading business data:', err);
                setError(extractApiErrorMessage(err, 'Failed to load business data'));
            } finally {
                setIsLoadingUsers(false);
            }
        };

        loadBusinessData();
    }, [selectedBusiness]);

    // Load selected user's fields
    useEffect(() => {
        if (!selectedUser) {
            setFields([]);
            return;
        }

        const loadFields = async () => {
            try {
                setIsLoadingFields(true);
                setError('');

                const userFields = await customUserDataFieldService.getUserFieldsByUserId(selectedUser.id);
                const mergedFields = buildMergedFields(userFields, businessFields, selectedUser.id);
                setFields(mergedFields);
            } catch (err) {
                console.error('Error loading user fields:', err);
                setError(extractApiErrorMessage(err, 'Failed to load user data fields'));
            } finally {
                setIsLoadingFields(false);
            }
        };

        loadFields();
    }, [selectedUser, businessFields]);

    const handleSaveField = async (businessDataFieldId: string, value: string) => {
        if (!selectedUser) return;
        try {
            await customUserDataFieldService.updateUserFieldForUser(selectedUser.id, businessDataFieldId, { value });
            setEditingField(null);

            // Reload fields
            const userFields = await customUserDataFieldService.getUserFieldsByUserId(selectedUser.id);
            const mergedFields = buildMergedFields(userFields, businessFields, selectedUser.id);
            setFields(mergedFields);
        } catch (err) {
            console.error('Error updating field:', err);
            setError(extractApiErrorMessage(err, 'Failed to update field'));
        }
    };

    const getFieldDisplayValue = (field: UserDataFieldDto): string => {
        const value = field.value?.trim();
        if (!value) return 'Not set';

        switch (field.businessDataField.type) {
            case BusinessDataFieldType.Bool:
                return value === 'true' ? 'Yes' : 'No';
            case BusinessDataFieldType.Text:
            case BusinessDataFieldType.MultilineText:
                return value.length > 50 ? `${value.substring(0, 50)}...` : value;
            default:
                return value;
        }
    };

    if (!user?.isAdmin) {
        return null;
    }

    return (
        <Box sx={{ maxWidth: 900, mx: 'auto' }}>
            <Typography variant="h5" gutterBottom>
                User Data Management
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                View and edit user data fields for any user across all businesses.
            </Typography>

            {error && (
                <Alert severity="error" sx={{ mb: 2, whiteSpace: 'pre-line' }} onClose={() => setError('')}>
                    {error}
                </Alert>
            )}

            <Paper sx={{ p: 3, mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                    Select Business
                </Typography>
                {isLoadingBusinesses ? (
                    <Box display="flex" justifyContent="center" p={2}>
                        <CircularProgress size={24} />
                    </Box>
                ) : (
                    <Autocomplete
                        options={businesses}
                        getOptionLabel={(option) => option.name}
                        value={selectedBusiness}
                        onChange={(_, newValue) => setSelectedBusiness(newValue)}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                placeholder="Search businesses..."
                                variant="outlined"
                                size="small"
                            />
                        )}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                    />
                )}
            </Paper>

            {selectedBusiness && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Select User
                    </Typography>
                    {isLoadingUsers ? (
                        <Box display="flex" justifyContent="center" p={2}>
                            <CircularProgress size={24} />
                        </Box>
                    ) : (
                        <Autocomplete
                            options={businessUsers}
                            getOptionLabel={(option) => `${option.firstName} ${option.lastName} (${option.email})`}
                            value={selectedUser}
                            onChange={(_, newValue) => setSelectedUser(newValue)}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    placeholder="Search by name or email..."
                                    variant="outlined"
                                    size="small"
                                />
                            )}
                            isOptionEqualToValue={(option, value) => option.id === value.id}
                        />
                    )}
                </Paper>
            )}

            {selectedUser && (
                <Paper sx={{ p: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                        Data Fields for {selectedUser.firstName} {selectedUser.lastName}
                    </Typography>

                    {isLoadingFields ? (
                        <Box display="flex" justifyContent="center" p={4}>
                            <CircularProgress />
                        </Box>
                    ) : fields.length === 0 ? (
                        <Typography variant="body2" color="text.secondary">
                            No user data fields have been configured for this business.
                        </Typography>
                    ) : (
                        <List>
                            {fields.map((field) => (
                                <ListItem
                                    key={field.businessDataFieldId}
                                    sx={{
                                        border: '1px solid',
                                        borderColor: 'divider',
                                        borderRadius: 1,
                                        mb: 1,
                                        '&:hover': {
                                            backgroundColor: 'action.hover',
                                        },
                                    }}
                                >
                                    <ListItemText
                                        secondaryTypographyProps={{ component: 'div' } as any}
                                        primary={
                                            <Box display="flex" alignItems="center" gap={1} mb={1}>
                                                <Typography variant="subtitle2" component="div">
                                                    {field.businessDataField.name}
                                                </Typography>
                                            </Box>
                                        }
                                        secondary={
                                            <Box>
                                                {field.businessDataField.description && (
                                                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                                        {field.businessDataField.description}
                                                    </Typography>
                                                )}
                                                <Typography variant="caption" display="block" color="text.secondary" gutterBottom>
                                                    Value:
                                                </Typography>
                                                <Typography variant="body1">
                                                    {getFieldDisplayValue(field)}
                                                </Typography>
                                            </Box>
                                        }
                                    />
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        startIcon={<EditIcon />}
                                        onClick={() => setEditingField(field)}
                                    >
                                        Edit
                                    </Button>
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Paper>
            )}

            {editingField && (
                <CustomFieldDialog
                    field={editingField}
                    open={!!editingField}
                    onClose={() => setEditingField(null)}
                    onSave={handleSaveField}
                />
            )}
        </Box>
    );
};

export default AdminUserDataPage;
